<template>
  <article>
    <activity-stage>
      <article slot="content">
        <div class="hearittypeit-container">
          <div class="hit-instructions">
            <audio-toggle-button></audio-toggle-button>
            <h1>{{ currentRound.instructions }}</h1>
          </div>
          <div class="content" v-if="showContent" transition="fade-05">
            <div class="left">
              <img
                v-if="currentRound.hearItTypeItImage"
                :src="currentRound.hearItTypeItImage?.image?.src"
                :alt="currentRound.hearItTypeItImage?.image?.description"
              />
            </div>
            <div class="right">
              <div v-for="(attempt, idx) in attempts" class="attempt" :key="attempt.key">
                <div
                  class="attempt-line"
                  :id="`attempt-${idx}`"
                  :transition="transition"
                  v-if="idx < attempts.length - 1 && (attempt.blinking || attempt.isCompleted)"
                >
                  <div class="attempt-box focusable" role="textbox" tabindex="0">
                    <div class="box-wrapper">
                      <p :class="['box-text', { blinking: attempt.blinking }]">
                        <span
                          aria-hidden="true"
                          :key="jdx + letter"
                          v-for="(letter, jdx) in attempt.text"
                          >{{ letter === ' ' ? '&nbsp;' : letter }}</span
                        >
                      </p>
                    </div>
                  </div>
                  <done-button
                    v-if="attempt.text && attempt.text.length > 0"
                    :class="[attempt.blinking ? '' : 'hidden']"
                    :on-click="onDone"
                    :transition="innerTransition"
                    text="Done"
                  >
                  </done-button>
                </div>

                <div
                  :transition="transition"
                  v-if="idx === attempts.length - 1 && (attempt.blinking || attempt.isCompleted)"
                >
                  <div class="attempt-box blinking-text">
                    <blinking-cursor-text
                      :round-id="progress.roundIndex"
                      :class-name="'box-text'"
                      :text="currentRound?.answer"
                      :text-step="0"
                    >
                    </blinking-cursor-text>
                  </div>
                </div>
              </div>

              <div
                v-if="showBox"
                class="did-you-hear-box"
                :transition="transition"
                @transitionend="onTryEnd"
              >
                <div class="hear-text">
                  <p class="hear-text">
                    {{ hearText }}
                  </p>
                </div>
                <img :src="didYouHear" alt="Did you hear" />
              </div>

              <div v-if="tryAgain" :transition="innerTransition" aria-live="assertive" role="alert">
                <button @click="onTryAgain" class="try-again">
                  <img :src="tryAgainButton" alt="" />
                  <span>Try Again</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </article>
    </activity-stage>
    <slot name="keyboard"></slot>
  </article>
</template>

<script>
import { TweenMax } from 'gsap';
import AssetLoader from 'mixins/AssetLoader';
import ActivityStage from 'components/activity/ActivityStage.vue';
import ActivityManager from 'mixins/ActivityManager';
import TypingActivityHelper from 'mixins/TypingActivityHelper';
import ErrorLogger from 'mixins/ErrorLogger';
import DoneButton from 'components/activity/DoneButton.vue';
import BlinkingCursorText from 'components/text-displays/BlinkingCursorText.vue';
import tryAgainButton from 'assets/images/activities/hear-it-type-it/tryAgainButton.png';
import didYouHear from 'assets/images/activities/hear-it-type-it/didYouHear.png';
import { HearItTypeItAnswerType } from 'graph/types/schema-generated-types';
import AudioToggleButton from 'components/buttons/AudioToggleButton';

const TIMEOUT = 30000;
const TRY_TIMEOUT = 3000;

export default {
  mixins: [AssetLoader, ActivityManager, ErrorLogger, TypingActivityHelper],

  components: {
    ActivityStage,
    DoneButton,
    BlinkingCursorText,
    AudioToggleButton
  },

  mounted() {
    this.keepInstructionsOnScreen(true);
    this.setFreeTextTyping(true);
  },

  data() {
    return {
      attempts: [],
      currentAttempt: 0,
      timeout: null,
      showBox: false,
      tryAgain: false,
      freeTextTyping: true,
      transition: 'fade-10',
      sounds: null,
      activeSound: null,
      showContent: false,
      tryAgainTimer: null,
      innerTransition: 'fade-10',
      hearText: '',
      tryAgainButton,
      didYouHear
    };
  },

  methods: {
    onTryEnd() {
      if (this.showDelayedTryAgain) {
        this.markCompleted();
        this.showDelayedTryAgain = false;
        this.tryAgain = true;
        this.disableKeyboard();
      }
    },

    getKeyboardConfig() {
      const { keyboardType, roofs } = this.activityData.keyboard || {};

      return {
        keyboardType: keyboardType || 'Keyboard35',
        roofs
      };
    },

    async loadActivity() {
      this.createRounds();
      await this.loadImages();

      const sounds = [];
      this.activityData.rounds.forEach(
        ({ answerAudio, instructionsAudio, affirmationAudio }, index) => {
          sounds.push({ roundId: index, audio: answerAudio.audio.src });
          sounds.push({ roundId: index, audio: instructionsAudio.audio.src });
          sounds.push({ roundId: index, audio: affirmationAudio.src });
        }
      );
      this.loadSounds(sounds);
      this.createAttemptsForCurrentRound(this.activityData.rounds?.[0]);
    },

    async loadSounds(sounds) {
      this.queueAudio(sounds.map(snd => snd.audio));
      const cache = await this.downloadAudio();
      this.sounds = sounds.map(sound => ({
        id: sound.roundId,
        audio: cache.get(sound.audio)
      }));
    },

    createAttemptsForCurrentRound({ numberOfAttempts }) {
      const attempts = [];

      for (let idx = 0; idx < numberOfAttempts + 1; idx += 1) {
        attempts.push({
          text: '',
          isCompleted: false,
          blinking: idx === 0,
          key: idx
        });
      }

      this.attempts = attempts;
    },

    setTimer() {
      this.showBox = false;
      clearTimeout(this.tryAgainTimer);
      clearTimeout(this.timeout);
      this.timeout = setTimeout(() => {
        this.showBox = true;
        // do not set timer for last attempt
        if (this.currentAttempt === this.attempts.length - 1) return;
        this.tryAgainTimer = setTimeout(() => {
          this.showDelayedTryAgain = true;
          this.showBox = false;
        }, TRY_TIMEOUT);
      }, TIMEOUT);
    },

    playAudioForRound(skipInstructions = false) {
      this.fadeInInstructions();
      this.setHearText();
      this.transition = 'fade-10';
      this.showContent = true;

      if (this.activeSound) {
        this.activeSound.stop();
      }

      const playInstructionsAndSound = () => {
        const sound = this.sounds.filter(snd => snd.id === this.progress.roundIndex);
        const instructionsAudio = sound[1]?.audio;
        const answerAudio = sound?.[0]?.audio;

        if (skipInstructions) {
          answerAudio.play();
          this.activeSound = answerAudio;
          return;
        }

        this.activeSound = instructionsAudio;
        this.setInstructionsAudios([this.activeSound, answerAudio]);
        instructionsAudio.play();

        const playCurrent = () => {
          // fade in new round
          TweenMax.delayedCall(0.5, () => {
            // timer for keyboard inactivity
            this.setTimer();
            this.enableKeyboard();
          });
        };

        instructionsAudio.on('stop', playCurrent);
        instructionsAudio.on('end', () => {
          this.activeSound = answerAudio;
          playCurrent();
        });
      };

      playInstructionsAndSound();
    },

    resetRound() {
      this.disableKeyboard();
      this.setupNewRound();
      this.setHearText();
      this.fadeInInstructions();
      this.setFreeTextTyping(true);
      this.createAttemptsForCurrentRound(this.currentRound);
      // play audio
      this.playAudioForRound();
      // reset values
      this.showBox = false;
      this.tryAgain = false;
      this.currentAttempt = 0;
      this.freeTextTyping = true;
    },

    startNewRound() {
      if (this.isEndOfActivity) {
        this.keepInstructionsOnScreen(false);
        const sound = this.sounds.filter(snd => snd.id === this.progress.roundIndex);
        const affirmationAudio = sound?.[2]?.audio;
        affirmationAudio?.play();
        this.completeActivity(false);
        return;
      }

      this.timeout = null;
      this.transition = '';
      this.fadeOutInstructions();

      if (this.activeSound) {
        this.activeSound.stop();
      }

      const playSounds = () => {
        const sound = this.sounds.filter(snd => snd.id === this.progress.roundIndex);
        const affirmationAudio = sound?.[2]?.audio;
        affirmationAudio?.play();
        const playAudio = () => {
          affirmationAudio.off('stop', playAudio);
          affirmationAudio.off('end', playAudio);
          this.showContent = false;
          TweenMax.delayedCall(0.5, this.resetRound);
        };
        affirmationAudio.on('stop', playAudio);
        affirmationAudio.on('end', playAudio);
      };

      playSounds();
    },

    onSolveButtonClick() {
      this.attempts[this.currentAttempt].text = '';

      const { answer } = this.currentRound;
      this.handleCorrectKey({ character: answer });

      this.onDone();
    },

    onTryAgain() {
      this.enableKeyboard();
      this.playAudioForRound(true);
      const lastFinishedList = this.attempts.filter(q => q.isCompleted);
      const lastFinished = lastFinishedList[lastFinishedList.length - 1].key;

      this.innerTransition = '';
      this.$nextTick(() => {
        // start blinking on attempt
        this.attempts = this.attempts.map((attempt, idx) => ({
          ...attempt,
          blinking: lastFinished + 1 === idx
        }));

        this.tryAgain = false;
        this.currentAttempt += 1;
        this.showDelayedTryAgain = false;

        if (this.currentAttempt === this.attempts.length - 1) {
          this.freeTextTyping = false;
          this.setFreeTextTyping(false);
          this.overwriteProgressText({
            currentText: this.currentRound?.answer
          });
        } else {
          this.setTimer();
        }

        this.$nextTick(() => {
          this.innerTransition = 'fade-10';
        });
      });
    },

    markCompleted() {
      const notFinished = this.attempts.filter(q => !q.isCompleted);
      const firstKey = notFinished[0].key;

      // mark activity as completed
      this.attempts = this.attempts.map(attempt =>
        attempt.key !== firstKey
          ? attempt
          : {
              ...attempt,
              isCompleted: true,
              blinking: false
            }
      );
    },

    setHearText() {
      const { answerType, answer } = this.currentRound;
      switch (answerType) {
        case HearItTypeItAnswerType.LETTER:
          this.hearText = `Did you hear the letter${answer?.trim().length > 1 ? 's' : ''}?`;
          return;
        case HearItTypeItAnswerType.WORD:
          this.hearText = 'Did you hear the word?';
          return;
        default:
        case HearItTypeItAnswerType.SENTENCE:
          this.hearText = 'Did you hear the sentence?';
      }
    },

    onDone() {
      this.showDelayedTryAgain = false;
      const { answerType, answer } = this.currentRound;
      const { text } = this.attempts[this.currentAttempt];

      let answeredProperly = false;

      switch (answerType) {
        case HearItTypeItAnswerType.LETTER: {
          const trimmedText = text.toLowerCase().replace(/ /g, '');
          const trimmedPhrase = answer?.toLowerCase()?.replace(/ /g, '');
          answeredProperly = trimmedText === trimmedPhrase;
          break;
        }
        case HearItTypeItAnswerType.WORD: {
          const lowerText = text.toLowerCase().trim();
          const lowerPhrase = answer?.toLowerCase()?.trim();
          answeredProperly = lowerText === lowerPhrase;
          break;
        }
        case HearItTypeItAnswerType.SENTENCE:
        default: {
          answeredProperly = text === answer;
          break;
        }
      }

      this.markCompleted();
      if (answeredProperly) {
        this.startNewRound();
        return;
      }

      this.tryAgain = true;
      this.disableKeyboard();
    },

    async loadImages() {
      this.activityData.rounds.forEach(({ image }) => this.queueImage(image));
      this.downloadImages();
    },

    fadeInInstructions() {
      TweenMax.to('.hit-instructions', 1, { opacity: 1 });
    },

    fadeOutInstructions() {
      TweenMax.to('.hit-instructions', 1, { opacity: 0 });
    },

    startActivity() {
      this.setupNewRound();
      this.errorLoggerStart();

      this.$nextTick(() => {
        this.setActivityContainerHeight();
        this.playAudioForRound();
      });
    },

    handleCorrectKey(attempt) {
      const { character } = attempt;
      this.showDelayedTryAgain = false;
      this.showBox = false;
      clearTimeout(this.tryAgainTimer);
      clearTimeout(this.timeout);

      if (!this.freeTextTyping) {
        this.sndType();
        this.incrementTypingIndex();

        this.captureKeyboardAttemptSegment(attempt, this.isEndOfCurrentSentence);

        if (this.isEndOfCurrentSentence) {
          this.completeStep();

          // activity got completed
          if (this.isEndOfActivity) {
            this.keepInstructionsOnScreen(false);
            this.startNewRound();
            return;
          }

          this.setCurrentStep();
          if (this.currentAttempt === this.attempts.length - 1) {
            this.startNewRound();
          }
        }

        return;
      }

      this.sndType();
      const lowerCh = character.toLowerCase();
      const noActionKeys = ['tab'];
      const { currentAttempt } = this;
      const currentText = this.attempts[currentAttempt].text;

      if (lowerCh === 'enter') {
        // enter acts the same as done button
        this.disableKeyboard();
        this.onDone();
        return;
      }

      if (currentText.length === 200) return;
      if (noActionKeys.includes(lowerCh)) return;
      if (lowerCh === 'backspace') {
        const end = currentText.length === 0 ? 0 : currentText.length - 1;
        this.attempts[currentAttempt].text = this.attempts[currentAttempt].text.substring(0, end);
      } else {
        this.attempts[currentAttempt].text = `${currentText}${character}`;
      }
    }
  }
};
</script>

<style lang="scss" scoped>
@import 'src/assets/sass/fade.scss';

.hidden {
  visibility: hidden;
}

.content {
  display: flex;
  flex-flow: row wrap;
  margin: 35px auto 0px auto;
  width: calc(100% - 20px);

  .left {
    flex-basis: calc(35% - 20px);
    padding-right: 20px;

    img {
      max-width: 100%;
      height: auto;
    }
  }
}

.attempt {
  margin-top: 10px;
}

.checkmark-icon {
  margin-left: 10px;
}

.attempt-box,
.checkmark-icon {
  vertical-align: middle;
  display: inline-block;
}

.box-wrapper {
  width: calc(475px - 24px);

  @media (max-width: 432px) {
    width: calc(315px - 24px);
  }
}

.attempt-box {
  border: 2px solid #000;
  box-sizing: border-box;
  min-height: 67.5px;
  overflow: hidden;
  padding: 13px 12px;
  position: relative;
  width: calc(475px - 24px);

  @media (max-width: 432px) {
    width: 325px;
  }

  .box-text {
    display: inline-block;
    font-size: 20px;
    min-height: 30px;
    position: relative;
    margin: 0;
    width: calc(445px - 24px);
    word-break: break-word;

    span {
      color: #000;
    }

    .blinking-text {
      color: initial;
    }

    &.blinking::after {
      content: '';
      position: absolute;
      width: 10px;
      height: 0;
      bottom: 2px;
      border-bottom: 2px solid #000;
      animation: blinky 0.65s step-end infinite;
      animation-iteration-count: infinite;
    }

    @media (max-width: 432px) {
      width: calc(315px - 24px);
    }
  }
}

.hear-text {
  top: 50%;
  left: 50%;
  z-index: 100;
  position: absolute;
  transform: translate(-50%, -50%);
  font-family: 'hwtfutura-bold';
  color: #000;
  width: 225px;

  p {
    text-align: center;
  }
}

.did-you-hear-box {
  position: relative;
  display: block;
  width: 475px;

  img {
    display: block;
    width: 270px;
    height: auto;
    margin-left: auto;
    margin-right: auto;
    position: relative;
    z-index: 20;
  }
}

.try-again {
  background: transparent;
  border: none;
  cursor: pointer;
  text-align: center;
  width: 475px;

  img {
    display: inline-block;
    width: 90px;
    height: auto;
  }

  span {
    display: block;
  }
}

@keyframes blinky {
  from,
  to {
    border-bottom: 2px solid #000;
  }

  50% {
    border-bottom: 0px solid #000;
  }
}

.hit-instructions {
  display: inline-block;
  font-family: 'hwtfutura';
  font-size: 18px;
  left: 15px;
  margin: 0px 0px 10px 0px;
  opacity: 0;

  top: 0;
  z-index: 10;
  -webkit-font-smoothing: auto;

  button {
    position: absolute;
  }

  h1 {
    font-family: hwtfutura;
    font-size: 18px;
    display: inline-block;
    left: 20px;
    margin: 0px 0px 0px 30px;
    position: relative;
    top: 10px;
    -webkit-font-smoothing: auto;
  }
}

#solve-button-container {
  button {
    background-color: black;
    border-radius: 6px;
    padding: 5px;
    text-align: center;

    span {
      color: white;
    }
  }
}

#activity-content {
  opacity: 0;
}

.chrome-os,
.tablet {
  .content {
    .left {
      flex-basis: calc(25% - 20px);
    }
  }
}
</style>
